import { Component, Inject, OnInit, ElementRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProviderportalService } from '../../providerportal.service';
import { CupService } from '../../../shared/services/cup.service';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface DisputeDialogData {
  claimId: string;
  claimData: any;
}

@Component({
  selector: 'app-dispute-dialog',
  templateUrl: './dispute-dialog.component.html',
  styleUrls: ['./dispute-dialog.component.scss'],
  standalone: false
})
export class DisputeDialogComponent implements OnInit {

  @ViewChild('disputeFormContent') disputeFormContent!: ElementRef;

  disputeForm: any = {
    disputeType: '',
    disputeReason: '',
    disputeDescription: '',
    expectedOutcome: '',
    providerName: '',
    title: '',
    address: '',
    regionCode: '',
    npi: ''
  };

  disputeTypes: Array<{ value: string; label: string }> = [];

  private readonly allDisputeTypes = [
    { value: '5017', label: 'Provider Payment Dispute I' },
    { value: '5018', label: 'Provider Dispute Decision II' }
  ];

  disputeReasons: Array<{ value: string; label: string; summary: string }> = [];

  isSubmitting = false;
  loadingAddress = false;
  contractCode: string = '';
  facetsMemberKey: string = '';
  memberProviderError: string = '';
  submissionError: string = '';
  disputeAlreadySubmitted = false;
  dcn: string = '';

  constructor(
    public dialogRef: MatDialogRef<DisputeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DisputeDialogData,
    private providerportalService: ProviderportalService,
    private cupService: CupService
  ) {
    this.dialogRef.disableClose = true; 
  }

  ngOnInit(): void {
    // Load dispute reason types from API
    (this.providerportalService.getproviderDisputeReasonType() as any).subscribe({
      next: (data: any[]) => {
        this.disputeReasons = data.map(item => ({
          value: item.queue,
          label: item.subtype,
          summary: item.summary
        }));
      },
      error: (err: any) => console.error('Error loading dispute reason types:', err)
    });

    // Check existing disputes for this claim to determine available dispute types
    this.providerportalService.isClaimDisputeExists(this.data.claimId).subscribe({
      next: (response: any) => this.applyDisputeTypeOptions(response?.disputes || []),
      error: (err: any) => {
        console.error('Error checking claim dispute existence:', err);
        this.disputeTypes = this.allDisputeTypes.filter(t => t.value === '5017');
      }
    });

    // Initialize form with data from claimData
    if (this.data.claimData) {
      const provider = this.data.claimData.provider;
      
      //console.log(this.data.claimData.details.memberId);
      
      //console.log('Dispute Dialog - Provider Data:', provider);
      
      // Map provider name from claimData.provider.name
      this.disputeForm.providerName = provider?.name || '';
      
      // Map NPI from claimData.provider.npi
      this.disputeForm.npi = provider?.npi || '';
      
      // Map region code
      this.disputeForm.regionCode = provider?.regionCode || 
                                     this.data.claimData.regionCode || '';
      
      
      this.disputeForm.address = '';

     
      const providerId = provider?.providerId;
      if (providerId) {
        this.loadingAddress = true;
        this.providerportalService.getEdhProviderDetails(providerId).subscribe({
          next: (response: any) => {
            const demographics = response?.providerDemographics?.[0];
            const addresses: any[] = demographics?.providerAddress || [];
            const rAddress = addresses.find(a => a.providerAddressType === 'R');
            if (rAddress) {
              const parts = [
                rAddress.providerAddress1,
                rAddress.providerCity,
                rAddress.providerState,
                rAddress.providerZip
              ].filter(Boolean);
              this.disputeForm.address = parts.join(', ');
            } else {
              this.disputeForm.address = '';
            }
            this.loadingAddress = false;
          },
          error: (err: any) => {
            console.error('Error fetching EDH provider details:', err);
            this.disputeForm.address = '';
            this.loadingAddress = false;
          }
        });
      }
    }
  }

  private applyDisputeTypeOptions(disputes: any[]): void {
    const codes = disputes.map(d => String(d.disputeTypeCode));
    if (codes.includes('5018')) {
      this.disputeTypes = [];
      this.disputeAlreadySubmitted = true;
      this.submissionError = 'A dispute has already been submitted for this claim.';
    } else if (codes.includes('5017')) {
      this.disputeTypes = this.allDisputeTypes.filter(t => t.value === '5018');
    } else {
      this.disputeTypes = this.allDisputeTypes.filter(t => t.value === '5017');
    }
  }

 

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.disputeAlreadySubmitted) {
      this.submissionError = 'A dispute has already been submitted for this claim.';
      return;
    }

    // Validate required fields
    if (!this.disputeForm.disputeType || !this.disputeForm.disputeReason || !this.disputeForm.disputeDescription || !this.disputeForm.expectedOutcome) {
      alert('Please fill in the required fields: Provider Dispute, Dispute Reason, Description and Expected Outcome');
      return;
    }

    const memberId = this.data.claimData?.details?.memberId;
    const providerId = this.data.claimData?.provider?.providerId;
    if (!memberId) {
      console.error('Member ID not available for EDH lookup');
      this.closeWithResult();
      return;
    }

    this.memberProviderError = '';
    this.submissionError = '';
    this.isSubmitting = true;

    // Check if a dispute already exists for this claim before proceeding
    this.providerportalService.isClaimDisputeExists(this.data.claimId).subscribe({
      next: (existsResponse: any) => {
        this.applyDisputeTypeOptions(existsResponse?.disputes || []);
        if (this.disputeAlreadySubmitted) {
          this.isSubmitting = false;
        } else {
          this.proceedWithSubmission(memberId, providerId);
        }
      },
      error: (err: any) => {
        console.error('Error checking claim dispute existence:', err);
        this.isSubmitting = false;
        this.submissionError = 'Unable to verify dispute status. Please try again.';
      }
    });
  }

  private proceedWithSubmission(memberId: string, providerId: string): void {
    this.providerportalService.getEDHMemberInfo(memberId).subscribe({
      next: (response: any) => {
        const memberInfo = response?.memberInfo?.[0];
        this.contractCode = memberInfo?.contractCode || '';
        const demographics = memberInfo?.memberDemographics?.[0];
        this.facetsMemberKey = demographics?.facetsMemberKey || '';
        //console.log('EDH Member Info - contractCode:', this.contractCode, 'facetsMemberKey:', this.facetsMemberKey);

        // Chain: check member-provider relationship
        this.providerportalService.checkMemberProvider(memberId, this.contractCode, providerId).subscribe({
          next: (checkResponse: any) => {
            if (checkResponse?.memberStatus === false) {
              this.isSubmitting = false;
              this.memberProviderError = 'Member ID Mismatch: The selected Member is not present in Facet.';
            } else {
              // providerStatus is true — generate PDF and upload
              this.generateAndUploadPDF(memberId, providerId);
            }
          },
          error: (err) => {
            console.error('Error checking member-provider:', err);
            this.isSubmitting = false;
            this.submissionError = 'Dispute submission failed. Please try again.';
          }
        });
      },
      error: (err) => {
        console.error('Error fetching EDH Member Info:', err);
        this.isSubmitting = false;
        this.submissionError = 'Dispute submission failed. Please try again.';
      }
    });
  }

  private generateDCN(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const trimmedClaimId = String(this.data.claimId).slice(-8);
    const claimSubTypeCd = this.data.claimData?.details?.claimSubTypeCd;
    // M = Medical -> HX, H = Hospital/Facility -> UX
    const dcnTypeCode = claimSubTypeCd === 'M' ? 'HX' : 'UX';
    return `${yyyy}${mm}${dd}${dcnTypeCode}${trimmedClaimId}`;
  }

  private buildPdfHtml(): string {
    const field = (label: string, value: string) => `
      <div style="margin-bottom:14px;">
        <div style="font-size:11px;color:#555;margin-bottom:3px;">${label}</div>
        <div style="font-size:13px;border:1px solid #bbb;border-radius:4px;padding:8px 10px;min-height:20px;">${value || '&nbsp;'}</div>
      </div>`;

    const selectedType   = this.disputeTypes.find(t => t.value === this.disputeForm.disputeType)?.label || '';
    const selectedReason = this.disputeReasons.find(r => r.value === this.disputeForm.disputeReason)?.label || '';

    return `
      <div style="font-family:Arial,sans-serif;padding:24px;color:#222;max-width:700px;">
        <h2 style="margin:0 0 20px;font-size:18px;border-bottom:2px solid #1976d2;padding-bottom:8px;color:#1976d2;">Provider Request Form</h2>

        <div style="display:flex;gap:16px;">
          <div style="flex:1;">${field('Claim Number', this.data.claimId || '')}</div>
          <div style="flex:1;">${field('Provider Dispute', selectedType)}</div>
        </div>

        ${field('Dispute Reason', selectedReason)}
        ${field('Description of Dispute', this.disputeForm.disputeDescription)}
        ${field('Expected Outcome', this.disputeForm.expectedOutcome)}

        <h3 style="font-size:14px;color:#1976d2;margin:20px 0 12px;border-bottom:1px solid #ddd;padding-bottom:4px;">Provider Information</h3>

        <div style="display:flex;gap:16px;">
          <div style="flex:1;">${field('Provider Name', this.disputeForm.providerName)}</div>
          <div style="flex:1;">${field('Title', this.disputeForm.title)}</div>
        </div>

        ${field('Address', this.disputeForm.address)}

        <div style="display:flex;gap:16px;">
          <div style="flex:1;">${field('Region Code', this.disputeForm.regionCode)}</div>
          <div style="flex:1;">${field('NPI', this.disputeForm.npi)}</div>
        </div>
      </div>`;
  }

  private generateAndUploadPDF(memberId: string, providerId: string): void {
    this.dcn = this.generateDCN();

    const userInfo = this.cupService.getUserInfoFromJWT();
    const userId = userInfo?.id || '';
    const userEmail = userInfo?.email || '';

    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:750px;background:#fff;';
    container.innerHTML = this.buildPdfHtml();
    document.body.appendChild(container);

    html2canvas(container, { scale: 2, useCORS: true, scrollX: 0, scrollY: 0 }).then((canvas: HTMLCanvasElement) => {
      document.body.removeChild(container);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const usablePageHeight = pageHeight - margin * 2;

      let yOffset = 0;
      while (yOffset < imgHeight) {
        const sliceHeight = Math.min(usablePageHeight, imgHeight - yOffset);
        const srcY = (yOffset / imgHeight) * canvas.height;
        const srcH = (sliceHeight / imgHeight) * canvas.height;

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = srcH;
        const ctx = pageCanvas.getContext('2d')!;
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

        const pageImgData = pageCanvas.toDataURL('image/png');
        if (yOffset > 0) { pdf.addPage(); }
        pdf.addImage(pageImgData, 'PNG', margin, margin, imgWidth, sliceHeight);

        yOffset += usablePageHeight;
      }

      const pdfBlob = pdf.output('blob');

      const documentPayload = {
        appId: 'PP',
        createdUser: userId || 'Test User',
        documentCode: 'DIS_CLAIM_DOC',
        txnLog: 'false',
        keyValuePairList: [
          { key: 'DCN',            value: this.dcn },
          { key: 'MemberID',       value: memberId },
          { key: 'ClaimID',        value: this.data.claimId },
          { key: 'ProviderID',     value: providerId },
          { key: 'ProviderEmailID',value: userEmail },
          { key: 'PortalUserID',   value: userId },
          { key: 'UserName',       value: userId }
        ]
      };

      this.providerportalService.uploadDisputeDocument(pdfBlob, documentPayload).subscribe({
        next: (uploadResponse: any) => {
          //console.log('Dispute document uploaded successfully:', uploadResponse);
          this.submitDisputeIntake(memberId, userId);
        },
        error: (err: any) => {
          console.error('Error uploading dispute document:', err);
          this.isSubmitting = false;
          this.submissionError = 'Dispute submission failed. Please try again.';
        }
      });
    }).catch((err: any) => {
      if (document.body.contains(container)) { document.body.removeChild(container); }
      console.error('Error generating PDF:', err);
      this.isSubmitting = false;
      this.dialogRef.close();
    });
  }

  private submitDisputeIntake(memberId: string, userId: string): void {
    const details = this.data.claimData?.details;
    const dos = (details?.firstDateOfService || '').split(' ')[0];
    const inputDate = dos;
    const [year, month, day] = inputDate.split('-');
    const formattedDate = `${month}/${day}/${year.slice(-2)}`;
    //console.log(formattedDate);
    const apapDesc = `DCN: ${this.dcn} // CLAIM ${this.data.claimId} // DOS: ${formattedDate}`;

    // Look up summary from the selected dispute reason
    const selectedReason = this.disputeReasons.find(r => r.value === this.disputeForm.disputeReason);
    const apapSummary = selectedReason?.summary || '';

    const intakePayload = {
      AppealIntake: {
        MEME_CK:        this.facetsMemberKey,
        CSPD_CAT:       'M',
        APAP_MCTR_CAT:  '5000',
        APAP_MCTR_TYPE: this.disputeForm.disputeType,
        APAP_MCTR_STYP: this.disputeForm.disputeReason,
        APAP_DESC:      apapDesc,
        APAP_SUMMARY:   apapSummary,
        APLV_MCTR_METH: '1004',
        APLV_USID_PRI:  'ProvPortal'
      },
      AppealLinks: [
        {
          APLK_TYPE:      'C',
          APLK_ID:        this.data.claimId,
          APLK_MCTR_REAS: '5002',
          APLK_DESC:      'CLAIM LINKED REC'
        }
      ]
    };

 this.providerportalService.submitClaimDisputeIntake(intakePayload).subscribe({
      next: (intakeResponse: any) => {
        //console.log('Claim dispute intake submitted successfully:', intakeResponse);
        const apapId = intakeResponse?.Data?.Appeals?.Appeal?.APAP_ID;
        if (apapId) {
          this.insertAppealNotes(apapId, memberId, userId);
        } else {
          this.isSubmitting = false;
          this.closeWithResult();
        }
      },
      error: (err: any) => {
        console.error('Error submitting claim dispute intake:', err);
        this.isSubmitting = false;
        this.submissionError = 'Dispute submission failed. Please try again.';
      }
    });
  }

  private insertAppealNotes(apapId: string, memberId: string, userId: string): void {
    // const notesPayload = {
    //   apaP_ID:             apapId,
    //   subscriberID:        memberId,
    //   contractID:          this.contractCode,
    //   disputeNote:         this.disputeForm.disputeDescription,
    //   expectedOutcomeNote: this.disputeForm.expectedOutcome,
    //   userName:            'ProvPortal'
    // };
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const notesPayload = {
    appealNotesInfo: {
    apaP_ID: apapId,
    subscriberID: memberId,
    contractID: this.contractCode,
    disputeNote: this.disputeForm.disputeDescription,
    expectedOutcomeNote: this.disputeForm.expectedOutcome,
    userName: 'ProvPortal'
  },
  administrativeLog: {
    date: `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}T00:00:00.000Z`,
    category: '5006', 
    subnet: '5011',
    dueDt: null, 
    status: 'Y',
    summary: ''
  }
};

    this.providerportalService.insertAppealNotes(notesPayload).subscribe({
      next: (notesResponse: any) => {
        //console.log('Appeal notes inserted successfully:', notesResponse);
        if(notesResponse?.code == 1){
        this.saveDisputeToDB(notesPayload.appealNotesInfo.apaP_ID, memberId, userId, notesPayload);

        }
        else{
          this.isSubmitting = false;
          this.submissionError = 'Dispute submission failed. Please try again.';
        }
      },
      error: (err: any) => {
        console.error('Error inserting appeal notes:', err);
        this.isSubmitting = false;
        this.submissionError = 'Dispute submission failed. Please try again.';
      }
    });
  }

  private saveDisputeToDB(apapId: string, memberId: string, userId: string, notesPayload: any): void {
    const details   = this.data.claimData?.details;
    const provider  = this.data.claimData?.provider;
    const userInfo  = this.cupService.getUserInfoFromJWT();
    const adminLog  = notesPayload.administrativeLog;

    const selectedType   = this.disputeTypes.find(t => t.value === this.disputeForm.disputeType);
    const selectedReason = this.disputeReasons.find(r => r.value === this.disputeForm.disputeReason);
    const dos = (details?.firstDateOfService || '').split(' ')[0];
    const inputDate = dos;
    const [year, month, day] = inputDate.split('-');
    const formattedDate = `${month}/${day}/${year.slice(-2)}`;
    //console.log("2---:",formattedDate);

    const dbPayload = {
      // Claim Details
      claimID:              this.data.claimId,
      claimReceivedDate:    details?.claimReceivedDate || null,
      firstDateOfService:   (details?.firstDateOfService || '').split(' ')[0] || null,
      diagnosisCodes:       this.data.claimData?.diagnosisCodes || null,
      billed:               details?.billed || null,
      payAmount:            details?.payAmount || null,
      statusCode:           details?.statusCode || null,
      statusDesc:           details?.statusDesc || null,
      networkInd:           details?.networkInd || null,
      capitationCode:       details?.capitationCode || null,
      capitationDesc:       details?.capitationDesc || null,

      // Member
      memberID:             memberId,
      memberName:           details?.memberName || null,
      facetsMemberKey:      this.facetsMemberKey || null,
      contractCode:         this.contractCode || null,

      // Provider
      providerID:           provider?.providerId || null,
      providerName:         provider?.name || null,
      providerNPI:          provider?.npi || null,
      providerAddress:      provider?.address || null,
      regionCode:           provider?.regionCode || null,

      // Portal User
      portalUserID:         userId || null,
      portalUserEmail:      userInfo?.email || null,
      userTitle:            this.disputeForm.title || null,

      // DCN
      dcn:                  this.dcn,

      // Dispute Form
      disputeTypeCode:      this.disputeForm.disputeType || null,
      disputeTypeLabel:     selectedType?.label || null,
      disputeReasonCode:    this.disputeForm.disputeReason || null,
      disputeReasonLabel:   selectedReason?.label || null,
      disputeReasonSummary: selectedReason?.summary || null,
      disputeDescription:   this.disputeForm.disputeDescription || null,
      expectedOutcome:      this.disputeForm.expectedOutcome || null,

      // Facets Intake (APAP)
      apapId:               apapId || null,
      apapDesc:             `DCN: ${this.dcn} // CLAIM ${this.data.claimId} // DOS: ${formattedDate}`,
      apapCategory:         '5000',
      apapMctrType:         this.disputeForm.disputeType || null,
      apapMctrStyp:         this.disputeForm.disputeReason || null,
      apapLevelMeth:        '1004',

      // Appeal Notes
      disputeNote:          this.disputeForm.disputeDescription || null,
      expectedOutcomeNote:  this.disputeForm.expectedOutcome || null,

      // Administrative Log
      adminLogDate:         adminLog?.date || null,
      adminLogCategory:     adminLog?.category || null,
      adminLogSubnet:       adminLog?.subnet || null,
      adminLogDueDate:      adminLog?.dueDt || null,
      adminLogStatus:       adminLog?.status || null,
      adminLogSummary:      adminLog?.summary || null,

      // Document
      documentCode:         'DIS_CLAIM_DOC',
      appID:                'PP',
      documentUploaded:     true
    };

    this.providerportalService.insertClaimDispute(dbPayload).subscribe({
      next: (res: any) => {
        //console.log('Claim dispute saved to DB successfully:', res);
        this.isSubmitting = false;
        this.closeWithResult();
      },
      error: (err: any) => {
        console.error('Error saving claim dispute to DB:', err);
        // Non-blocking: still close successfully since Facets submission succeeded
        this.isSubmitting = false;
        this.closeWithResult();
      }
    });
  }

  private closeWithResult(): void {
    this.dialogRef.close({
      claimId: this.data.claimId,
      disputeData: this.disputeForm,
      contractCode: this.contractCode,
      facetsMemberKey: this.facetsMemberKey,
      dcn: this.dcn
    });
  }
}
